'use client'

import { AuthSignedIn } from '@/components/auth/auth-signed-in'
import { AuthSignedOut } from '@/components/auth/auth-signed-out'
import { AppRoutes } from '@/routes'
import { Button } from '@/shared/ui/button'
import { cn } from '@/utils/cn'
import Image from 'next/image'
import Link from 'next/link'
import { HTMLAttributes } from 'react'
import { NotificationCancelConfirmDialog } from '@/components/notification-cancel-confirm-dialog'
import { useAlarmTooltipShow } from '../../hooks/use-alarm-tooltip-show'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/ui/tooltip'
import { useGAPurchase } from '../../hooks/use-ga-purchase'
import { useProductDetailFooter } from './use-product-detail-footer'
import { useDrawerStore } from '@/providers/drawer-store-provider'
import { DrawerOptions } from '@/hooks/use-drawer-state'
export interface ProductDetailFooterProps
  extends HTMLAttributes<HTMLDivElement> {
  productId: string
}

export const ProductDetailFooter = ({
  productId,
  className,
  ...props
}: ProductDetailFooterProps) => {
  const { show } = useAlarmTooltipShow()
  const { gaPurchase } = useGAPurchase({ productId })
  const { data: productDetailFooter, pending } = useProductDetailFooter({
    productId,
  })
  const { onClickDrawer } = useDrawerStore((state) => state)
  if (pending) return null
  if (!productDetailFooter) return null

  const { price, priceLowest, linkUrl, notificationId } = productDetailFooter

  return (
    <footer
      className={cn(className, 'flex items-center gap-4 px-5 pb-5 pt-2')}
      {...props}
    >
      <TooltipProvider>
        <Tooltip open={show && !notificationId}>
          <TooltipTrigger>
            <AuthSignedOut>
              <Link
                href={AppRoutes.signIn(AppRoutes.product(productId))}
                className={cn(
                  'flex size-[60px] items-center justify-center rounded-lg bg-gray-100',
                  'bg-black',
                )}
              >
                <Image
                  src={'/icons/상품상세_알림_off.svg'}
                  width={38}
                  height={38}
                  className="size-[38px]"
                  alt="열기"
                />
              </Link>
            </AuthSignedOut>
            <AuthSignedIn>
              <NotificationBottomSheet
                productId={productId}
                notificationId={notificationId}
                price={price}
                priceLowest={priceLowest}
                onClickDrawer={onClickDrawer}
              />
            </AuthSignedIn>
          </TooltipTrigger>
          <Button
            className="h-[60px] w-full text-body-01-semi text-white"
            variant="primary"
            asChild
          >
            <Link href={linkUrl} target="_blank" onClick={gaPurchase}>
              쿠팡에서 구매하기
            </Link>
          </Button>
          <TooltipContent align="start">
            알림을 설정하고 최저가로 상품을 구매해보세요!
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </footer>
  )
}

export const NotificationBottomSheet = ({
  productId,
  notificationId,
  price,
  priceLowest,
  onClickDrawer,
}: {
  productId: string
  notificationId?: string
  price: number
  priceLowest: number
  onClickDrawer: (drawerOptions: DrawerOptions) => void
}) => {
  return (
    <>
      {notificationId ? (
        <NotificationCancelConfirmDialog
          productId={productId}
          notificationId={notificationId}
          trigger={
            <Button
              className={cn(
                'flex size-[60px] items-center justify-center rounded-lg bg-gray-4 p-0',
              )}
            >
              <Image
                src={'/icons/상품상세_알림_on.svg'}
                width={38}
                height={38}
                className="size-[38px]"
                alt="열기"
              />
            </Button>
          }
        />
      ) : (
        <Button
          className={cn(
            'flex size-[60px] items-center justify-center rounded-lg bg-black p-0',
          )}
          onClick={(e) => {
            e.preventDefault()
            onClickDrawer({ price, priceLowest, productId })
          }}
        >
          <Image
            src={'/icons/상품상세_알림_off.svg'}
            width={38}
            height={38}
            className="size-[38px]"
            alt="열기"
          />
        </Button>
      )}
    </>
  )
}
